import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/intent-classifier/events/v1/event_types.pb';
import { SenderType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { Direction, RelatedType, Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS, SMSTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { AnimatePresence, motion } from 'motion/react';
import { TextWritebacksQueries } from '@frontend/api-messaging';
import { SMSDataSharedTypes, SMSDataV4 } from '@frontend/api-sms-data';
import { UsersQueries } from '@frontend/api-users';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PdfPreview, VideoPreview } from '@frontend/messaging-media-lightbox';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useAppFlagStore } from '@frontend/shared';
import { sentenceCase } from '@frontend/string';
import { TagSelectionComponents, TagSelectionHooks } from '@frontend/tag-selection';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  Avatar,
  AvatarProps,
  Chat,
  ChatTypes,
  Checkbox,
  KeyNames,
  useAlert,
  useDebouncedValue,
  useFormField,
  useTooltip,
} from '@frontend/design-system';
import { SMART_TAG_FEEDBACK_THRESHOLDS } from '../../constants';
import { useGetSortedTagsById, useSMSContextActions } from '../../hooks';
import { SMSContextActions } from '../../types';
import { DeleteSmsConfirmationModal } from '../modals/delete-sms-confirmation-modal';
import { SMSTask } from './sms-task';
import { TextWritebackStatus } from './text-writeback-status';
import { ThreadItemTag } from './thread-item-tag';

type AdditionalProps = Omit<
  ComponentProps<typeof Chat>,
  'direction' | 'avatar' | 'timestamp' | 'senderName' | 'error' | 'text' | 'textColor' | 'isSending' | 'children'
>;

type SMSThreadItemProps = {
  sms: SMS;
  avatarProps: Omit<AvatarProps, 'locationId' | 'personId' | 'isUser' | 'isWeave'> & { preferredName?: string };
  media: { mediaId: string; src?: string; contentType?: string }[];
  mediaMaxWidth?: string;
  isSelecting?: boolean;
  isSelected?: boolean;
  onToggleSmsSelection?: () => void;
  selectionDisabled?: boolean;
  hiddenContextActions?: SMSContextActions[];
  hideWritebacks?: boolean;
  hasNewWriteback: boolean;
  /** Fallback personId from thread's contact when sms.personId is empty */
  threadPersonId?: string;
} & AdditionalProps;

export const SMSThreadItem = forwardRef<HTMLUListElement, SMSThreadItemProps>(
  (
    {
      sms,
      avatarProps,
      media,
      mediaMaxWidth = '80%',
      highlightProps,
      contactName,
      isSelecting,
      isSelected,
      onToggleSmsSelection,
      selectionDisabled,
      hiddenContextActions,
      hideWritebacks,
      hasNewWriteback,
      threadPersonId,
      ...rest
    },
    ref
  ) => {
    const { selectedOrgId } = useAppScopeStore();
    const alert = useAlert();
    const { navigate: settingsNavigate } = useSettingsNavigate();
    const { t } = useTranslation('thread-body');
    const user = getUser();
    const isInbound = sms.direction === Direction.DIRECTION_INBOUND;
    const formatTimestamp = useTimestampFormatter();
    const senderName = `${avatarProps.firstName ?? ''} ${avatarProps.lastName ?? ''}`.trim() || avatarProps.name;
    const isSending = sms.statusDetails === SMSDataSharedTypes.OptimisticUpdateStatusDetails.SENDING;
    const isDeleting = sms.statusDetails === SMSDataSharedTypes.OptimisticUpdateStatusDetails.DELETING;
    const hasError =
      (sms.status === Status.STATUS_NOT_SENT &&
        sms.statusDetails !== SMSDataSharedTypes.OptimisticUpdateStatusDetails.SENDING) ||
      sms.status === Status.STATUS_ERROR;
    const resolvedTimestampText = isSending
      ? t('Sending...')
      : isDeleting
      ? t('Deleting...')
      : formatTimestamp(sms.createdAt);
    const [tagSearchValue, setTagSearchValue] = useState('');
    const [lightboxMedia, setLightboxMedia] = useState<
      | { kind: 'pdf'; mediaId: string; src: string; filename?: string }
      | { kind: 'video'; mediaId: string; src: string }
      | null
    >(null);
    const tagSelectionInputRef = useRef<HTMLInputElement>(null);
    const tagInputWidthCalcRef = useRef<HTMLSpanElement>(null);
    const [tagInputWidth, setTagInputWidth] = useState(0);
    const sortedSmsTagIds = useGetSortedTagsById({
      orgId: selectedOrgId,
      locationIds: [sms.locationId],
      tagIds: sms.tags,
      enabled: !hiddenContextActions?.includes('tag'),
    });

    const smsDataV4ApplyTag = SMSDataV4.Mutations.useApplyTagMutation();

    const applyTag = useCallback(
      async (tag: Tag) => {
        await smsDataV4ApplyTag.mutateAsync(
          {
            tagId: tag.id || tag.smartTagId,
            id: sms.id,
            createdAt: sms.createdAt,
            locationId: sms.locationId,
            threadId: sms.threadId,
          },
          {
            onError: () => {
              alert.error(t('Error tagging message.'));
            },
          }
        );
      },
      [sms, t, alert, smsDataV4ApplyTag]
    );

    const {
      modalProps: feedbackModalProps,
      onFeedbackClick,
      closeModal: closeFeedbackModal,
      userEntitiesWithFeedback,
    } = TagSelectionHooks.useTagFeedbackFlow({
      feedbackClickThreshold: SMART_TAG_FEEDBACK_THRESHOLDS,
      trackingIdPrefix: 'inbox',
    });
    const smsIdsWithFeedback = useMemo(
      () => userEntitiesWithFeedback[user?.userID ?? '']?.[EntityType.ENTITY_TYPE_SMS] ?? [],
      [user?.userID, userEntitiesWithFeedback]
    );
    const hideFeedbackFlow = useMemo(
      () => (user?.userID ? smsIdsWithFeedback.includes(sms.id) ?? false : false),
      [user?.userID, smsIdsWithFeedback, sms.id]
    );
    const {
      popoverProps: tagPopoverProps,
      open: openTagSelection,
      isOpen: tagSelectionIsOpen,
      close: closeTagSelection,
      getTriggerProps: getTagSelectionTriggerProps,
    } = TagSelectionHooks.useTagSelectionPopover({
      groupIds: [sms.locationId],
      onTagSelect: async (tag) => {
        await applyTag(tag);
        return;
      },
      onTagCreate: (initName) => {
        settingsNavigate({
          to: '/tags/quick-create',
          context: {
            onCreate: (tag: Tag) => applyTag(tag),
            name: initName,
          },
        });
      },
      trackingIds: {
        selectTag: `${InboxPrefixes.Tag}-add-existing-tag`,
        createTag: `${InboxPrefixes.Tag}-create-new-tag`,
        createNamedTag: `${InboxPrefixes.Tag}-create-new-tag-with-name`,
        openTagSettings: `${InboxPrefixes.Tag}-open-tag-settings`,
      },
      searchValue: tagSearchValue,
      tagIdsToExclude: sms.tags,
    });

    const smsDataV4DismissTag = SMSDataV4.Hooks.useDismissTagWithUndo({
      onUndoAvailable: (onClick, autoDismissAfter) => {
        alert.info({
          message: t('Tag Removed'),
          action: {
            label: t('Undo'),
            onClick,
          },
          autoDismissAfter,
        });
      },
      onDismissError: () => {
        alert.error('Error removing tag. Please try again.');
      },
    });

    const dismissTag = useCallback(
      (tag: SMSTag) => {
        smsDataV4DismissTag({
          tagId: tag.tagId,
          id: sms.id,
          locationId: sms.locationId,
          threadId: sms.threadId,
          originalAppliedBy: tag.appliedBy,
          smsCreatedAt: sms.createdAt,
        });
      },
      [sms]
    );

    const { getFeatureFlagValue } = useAppFlagStore();
    const hasInboxTasksEnabled = getFeatureFlagValue({
      locationIds: [sms.locationId],
      flagName: 'nwx:inbox-tasks',
      strategy: 'ALL',
    });
    const mmsEnhancementsEnabled = !!getFeatureFlagValue({
      locationIds: [sms.locationId],
      flagName: 'nwx:inbox-mms-enhancements',
      strategy: 'ALL',
    });

    const pdfMedia = useMemo(
      () =>
        media.find(({ mediaId, contentType }) => {
          if (contentType === 'application/pdf') return true;
          const filename = sms.media?.find((item) => item.mediaId === mediaId)?.filename;
          return !!filename && filename.toLowerCase().endsWith('.pdf');
        }),
      [media, sms.media]
    );

    const videoMedia = useMemo(
      () =>
        media.find(({ mediaId, contentType }) => {
          if (contentType?.startsWith('video/')) return true;
          const filename = sms.media?.find((item) => item.mediaId === mediaId)?.filename?.toLowerCase();
          return !!filename && (filename.endsWith('.mp4') || filename.endsWith('.mov'));
        }),
      [media, sms.media]
    );

    // PDFs and videos are files — copy actions don't apply since they have their own Download action.
    const isFileBubble = !!(pdfMedia?.mediaId || videoMedia?.mediaId);
    const mergedHiddenActions = useMemo<SMSContextActions[] | undefined>(() => {
      if (!isFileBubble) return hiddenContextActions;
      const base = hiddenContextActions ?? [];
      const extras: SMSContextActions[] = ['copy', 'copy-image', 'download-image'];
      return Array.from(new Set([...base, ...extras]));
    }, [hiddenContextActions, isFileBubble]);

    const { getActions, handleDownloadImage, deleteConfirmationModalProps, handleDeleteConfirmation } =
      useSMSContextActions({
        sms,
        openTagSelection,
        hiddenActions: mergedHiddenActions,
        threadPersonId,
      });
    const { primaryActions: basePrimaryActions, moreActions } = getActions();

    const primaryActions = useMemo<ChatTypes.PrimaryAction[]>(() => {
      if (pdfMedia?.mediaId) {
        return [
          {
            label: t('Download PDF'),
            icon: 'download',
            onClick: () => handleDownloadImage(pdfMedia.mediaId, 'file'),
            trackingId: `${InboxPrefixes.Thread}-message-action-download-pdf`,
          },
          ...basePrimaryActions,
        ];
      }
      if (videoMedia?.mediaId) {
        return [
          {
            label: t('Download Video'),
            icon: 'download',
            onClick: () => handleDownloadImage(videoMedia.mediaId, 'video'),
            trackingId: `${InboxPrefixes.Thread}-message-action-download-video`,
          },
          ...basePrimaryActions,
        ];
      }
      return basePrimaryActions;
    }, [pdfMedia, videoMedia, basePrimaryActions, handleDownloadImage, t]);
    const contextActions = useMemo(() => {
      const result: ComponentProps<typeof Chat.Bubble>['contextActions'] = [];
      primaryActions.forEach(({ icon, ...action }) => {
        result.push({
          ...action,
          Icon: () => <Icon name={icon} size={18} />,
        });
      });
      moreActions.forEach((action) => {
        result.push(action);
      });

      return result;
    }, [primaryActions, moreActions]);

    const locationUsersQueries = UsersQueries.useGetMultiLocationsUsers({
      locationIds: [sms.locationId],
      params: { showChildren: true },
    });

    const locationUsers = useMemo(() => locationUsersQueries.flatMap(({ data }) => data || []), [locationUsersQueries]);

    const deleterName = useMemo(() => {
      if (!sms.deletedBy) return undefined;

      if (user && sms.deletedBy === user?.userID) {
        return getFullName(user);
      }

      const matchedUser = locationUsers.find((user) => user.UserID === sms.deletedBy);
      if (!matchedUser) return t('Unknown User');

      return getFullName(matchedUser);
    }, [sms.deletedBy, user, locationUsers, t]);

    const sendingUser = useMemo(() => {
      if (sms.createdBy === user?.userID) {
        return {
          firstName: user.firstName,
          lastName: user.lastName,
        };
      }
      const foundUser = locationUsers.find((user) => user.UserID === sms.createdBy);
      if (!foundUser) return undefined;
      return {
        firstName: foundUser.FirstName,
        lastName: foundUser.LastName,
      };
    }, [sms.createdBy, user, locationUsers]);

    const checkboxProps = useFormField(
      {
        type: 'checkbox',
        value: isSelected,
      },
      [isSelected]
    );

    const debouncedIsSelecting = useDebouncedValue(isSelecting, 300);
    const onlyDefaultTagId =
      sortedSmsTagIds?.length === 1 && !sms.tagsDetailed?.find((t) => t.tagId === sortedSmsTagIds[0].id)?.appliedBy
        ? sortedSmsTagIds[0].id
        : undefined;

    const hasWriteback =
      !hideWritebacks &&
      sms.relatedIds.some((relatedId) => relatedId.type === RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE);
    const { data: noteStatus } = TextWritebacksQueries.useNoteStatus(
      { smsId: sms.id, locationId: sms.locationId },
      {
        enabled: hasWriteback,
        select: (data) => data.noteStatus,
      }
    );
    const { Tooltip, tooltipProps, triggerProps } = useTooltip({
      placement: sms.direction === Direction.DIRECTION_INBOUND ? 'top-end' : 'top-start',
      trigger: 'hover',
    });

    useEffect(() => {
      setTagInputWidth(tagInputWidthCalcRef.current?.offsetWidth ?? 0);
    }, [tagSearchValue]);

    return (
      <motion.div
        role={isSelecting ? 'button' : undefined}
        tabIndex={isSelecting ? 0 : -1}
        onClickCapture={(e) => {
          if (isSelecting) {
            e.stopPropagation();
            e.preventDefault();
            if (!selectionDisabled) onToggleSmsSelection?.();
          }
        }}
        onFocusCapture={(e) => {
          if (isSelecting) {
            e.stopPropagation();
            e.preventDefault();
          }
        }}
        css={[
          {
            display: 'flex',
            alignItems: 'center',
            transition: 'background-color 300ms ease-in-out, padding 300ms ease-in-out, margin 300ms ease-in-out',
          },
          isSelecting &&
            (selectionDisabled
              ? {
                  cursor: 'not-allowed',
                }
              : {
                  cursor: 'pointer',
                  // Add padding for background color without shifting the content
                  padding: theme.spacing(2, 0, 0),
                  margin: theme.spacing(-2, 0, 0),
                  ':hover': {
                    backgroundColor: theme.colors.neutral10,
                  },
                }),
        ]}
      >
        <AnimatePresence>
          {isSelecting && (
            <motion.div
              initial={
                debouncedIsSelecting
                  ? { width: 35, opacity: 1, paddingLeft: theme.spacing(1.5) }
                  : { width: 0, opacity: 0, paddingLeft: 0 }
              }
              animate={{ width: 35, opacity: 1, paddingLeft: theme.spacing(1.5) }}
              exit={{ width: 0, opacity: 0, paddingLeft: 0 }}
              css={{
                // Center the checkbox vertically on the bubble, not the whole row
                paddingBottom: theme.spacing(2),
              }}
            >
              <Checkbox
                {...checkboxProps}
                name={`select-${sms.id}`}
                error={false}
                disabled={selectionDisabled}
                data-value={checkboxProps.value}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <div
          css={[
            { width: '100%' },
            // Prevent pointer events on the message bubble if in selection mode
            isSelecting && {
              pointerEvents: 'none',
            },
          ]}
        >
          <Chat
            direction={isInbound ? 'inbound' : 'outbound'}
            defaultTagsWidth={sms?.tasks ? 150 : undefined}
            avatar={
              isInbound ? (
                <Photos.ContactProfilePhoto
                  {...avatarProps}
                  personId={sms.personId}
                  locationId={sms.locationId}
                  firstName={contactName?.firstName}
                  lastName={contactName?.lastName}
                  name={getFullName(contactName ?? {})}
                />
              ) : sms.senderType === SenderType_Enum.AI || sms.senderType === SenderType_Enum.PARTNER_AI ? (
                <Avatar size='small' isAiAssistant={true} title={t('AI Receptionist')} />
              ) : (
                <Avatar
                  {...avatarProps}
                  isUser
                  isWeave={!!sms.autogeneratedBy}
                  firstName={sendingUser?.firstName}
                  lastName={sendingUser?.lastName}
                  name={!!sms.autogeneratedBy ? t('Auto-Message') : getFullName(sendingUser ?? {})}
                />
              )
            }
            timestamp={resolvedTimestampText}
            senderName={isInbound ? getFullName(contactName ?? {}) : getFullName(sendingUser ?? {}) || senderName}
            text={
              deleterName
                ? t('This message was deleted on {{date}} by {{deleterName}}', {
                    date: formatTimestamp(sms.deletedAt),
                    deleterName,
                  })
                : sms.body
            }
            textColor={deleterName ? 'light' : undefined}
            isSending={isSending}
            primaryActions={primaryActions}
            error={hasError ? t('Not Delivered') : undefined}
            retrySend={getActions().moreActions.find((action) => action.label === t('Retry Send'))?.onClick} // TODO: fix this by enabling multiple primary actions
            moreActions={moreActions.length ? moreActions : undefined}
            StatusBar={
              hasError ? (
                <>
                  <span
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                    }}
                    {...triggerProps}
                  >
                    <Icon name='alert-invert-small' color='error' />
                  </span>
                  <Tooltip {...tooltipProps}>{[t('Not delivered'), sentenceCase(sms.statusDetails)].join(' ')}</Tooltip>
                </>
              ) : hasWriteback && !!noteStatus ? (
                <TextWritebackStatus showLabel={hasNewWriteback} noteStatus={noteStatus} direction={sms.direction} />
              ) : undefined
            }
            {...rest}
            ref={ref}
          >
            {!!(sms.deletedAt || sms.body) && (
              <Chat.Bubble
                backgroundColor={!!sms.autogeneratedBy ? theme.colors.success5 : undefined}
                variant={sms.deletedAt ? 'deleted' : 'text'}
                text={
                  sms.deletedAt
                    ? t('This message was deleted on {{date}} by {{name}}', {
                        date: new Date(sms.deletedAt).toLocaleDateString(),
                        name: deleterName || t('Unknown User'),
                      })
                    : sms.body
                }
                contextActions={contextActions}
              />
            )}
            {!sms.deletedAt &&
              media.map(({ mediaId, src, contentType }) => {
                const mediaItem = sms.media?.find((item) => item.mediaId === mediaId);
                const filename = mediaItem?.filename;
                const lowerFilename = filename?.toLowerCase();
                const isPdf =
                  mmsEnhancementsEnabled &&
                  (contentType === 'application/pdf' || (!!lowerFilename && lowerFilename.endsWith('.pdf')));
                const isVideo =
                  mmsEnhancementsEnabled &&
                  (contentType?.startsWith('video/') ||
                    (!!lowerFilename && (lowerFilename.endsWith('.mp4') || lowerFilename.endsWith('.mov'))));
                const isUnsupportedMedia =
                  !mmsEnhancementsEnabled &&
                  (contentType === 'application/pdf' ||
                    contentType?.startsWith('video/') ||
                    (!!lowerFilename &&
                      (lowerFilename.endsWith('.pdf') ||
                        lowerFilename.endsWith('.mp4') ||
                        lowerFilename.endsWith('.mov'))));
                if (isUnsupportedMedia) return null;
                const actions = getActions(mediaId);
                if (isPdf) {
                  return (
                    <Chat.Bubble
                      variant='pdf'
                      key={mediaId}
                      src={src ?? ''}
                      contextActions={actions.moreActions}
                      maxWidth={mediaMaxWidth}
                      contextMenuReturnFocus={false}
                      pdfProps={{
                        filename,
                        onClickPreview: () => setLightboxMedia({ kind: 'pdf', mediaId, src: src ?? '', filename }),
                      }}
                    />
                  );
                }
                if (isVideo) {
                  return (
                    <Chat.Bubble
                      variant='video'
                      key={mediaId}
                      src={src ?? ''}
                      contextActions={actions.moreActions}
                      maxWidth={mediaMaxWidth}
                      contextMenuReturnFocus={false}
                      videoProps={{
                        onClickPreview: () => setLightboxMedia({ kind: 'video', mediaId, src: src ?? '' }),
                      }}
                    />
                  );
                }
                return (
                  <Chat.Bubble
                    variant='image'
                    key={mediaId}
                    src={src}
                    loadingText={!mediaId ? t('Loading image...') : undefined}
                    contextActions={actions.moreActions}
                    maxWidth={mediaMaxWidth}
                    contextMenuReturnFocus={false}
                  />
                );
              })}
            {hasInboxTasksEnabled && !!sms?.tasks?.length && <SMSTask tasks={sms?.tasks} />}
            {!hiddenContextActions?.includes('tag') &&
              sms.tagsDetailed?.map((tag) => (
                <ThreadItemTag
                  key={`${tag.tagId}-${sms.id}`}
                  tagId={tag.tagId}
                  tag={sortedSmsTagIds?.find((t) => t.id === tag.tagId)}
                  removeTag={() => dismissTag(tag)}
                  showFeedbackPopover={sms.tagsDetailed?.length === 1 && !hideFeedbackFlow}
                  isAutoApplied={!tag.appliedBy}
                  hasTagsFeedbackEndAdornment={!hideFeedbackFlow && !!onlyDefaultTagId}
                  groupId={sms.locationId}
                  onSmartTagFeedbackClick={(context) =>
                    onFeedbackClick({ ...context, entityType: EntityType.ENTITY_TYPE_SMS, entityId: sms.id })
                  }
                />
              ))}
            {!hiddenContextActions?.includes('tag') && tagSelectionIsOpen && (
              <Chat.Tag
                color='gray'
                label={
                  <span ref={getTagSelectionTriggerProps().ref}>
                    <input
                      ref={tagSelectionInputRef}
                      type='text'
                      value={tagSearchValue}
                      autoFocus
                      onChange={(e) => setTagSearchValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === KeyNames.Escape) {
                          closeTagSelection();
                        }
                      }}
                      css={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        outline: 'none',
                        appearance: 'none',
                        width: `calc(${theme.spacing(1)} + ${tagInputWidth}px)`,
                        ':active': {
                          outline: 'none',
                          border: 'none',
                        },
                      }}
                    />
                  </span>
                }
              />
            )}
          </Chat>
          {
            /* Invisible element to calculate the dynamic width of the tag input */
            tagSelectionIsOpen && (
              <span
                ref={tagInputWidthCalcRef}
                css={{
                  position: 'absolute',
                  opacity: 0,
                  fontSize: theme.fontSize(14),
                }}
                tabIndex={-1}
              >
                {tagSearchValue}
              </span>
            )
          }
          <TagSelectionComponents.TagSelectionPopover {...tagPopoverProps} returnFocus={false} initialFocus={-1} />
          <TagSelectionComponents.TagFeedbackModal
            {...feedbackModalProps}
            trackingIdPrefix='inbox'
            onCancel={closeFeedbackModal}
            onSkip={() => feedbackModalProps.onSubmit('')}
          />
          <DeleteSmsConfirmationModal modalProps={deleteConfirmationModalProps} onConfirm={handleDeleteConfirmation} />
          {lightboxMedia?.kind === 'pdf' && (
            <PdfPreview
              show
              src={lightboxMedia.src}
              filename={lightboxMedia.filename}
              senderName={isInbound ? getFullName(contactName ?? {}) : getFullName(sendingUser ?? {}) || senderName}
              timestamp={resolvedTimestampText}
              onClose={() => setLightboxMedia(null)}
              onDownload={() => handleDownloadImage(lightboxMedia.mediaId, 'file')}
            />
          )}
          {lightboxMedia?.kind === 'video' && (
            <VideoPreview
              show
              src={lightboxMedia.src}
              senderName={isInbound ? getFullName(contactName ?? {}) : getFullName(sendingUser ?? {}) || senderName}
              timestamp={resolvedTimestampText}
              onClose={() => setLightboxMedia(null)}
              onDownload={() => handleDownloadImage(lightboxMedia.mediaId, 'video')}
            />
          )}
        </div>
      </motion.div>
    );
  }
);
SMSThreadItem.displayName = 'SMSItem';
